import { Handshake } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import type { Coproducer, Product } from '@entities/product/api/product.contract'
import {
  coproductionForProductQuery,
  inviteToCoproducer,
  percentageLabel,
} from '@entities/partnership'
import { StatusBadge } from '@shared/design-system/ui'
import {
  funnelCoproductionNotice,
  funnelCoproductionNoticeBody,
  funnelCoproductionNoticeFootnote,
  funnelCoproductionNoticeHeader,
  funnelCoproductionNoticeIcon,
  funnelCoproductionNoticeTitle,
  funnelStageNotice,
  funnelStageNoticeIcon,
} from './funnel.css'

function currentCoproducer(coproducers: Coproducer[]) {
  return (
    coproducers.find((coproducer) => coproducer.status === 'ATIVO') ??
    coproducers.find((coproducer) => coproducer.status === 'PENDENTE')
  )
}

export function FunnelCoproductionNotice({ coproducers }: { coproducers: Coproducer[] }) {
  const coproducer = currentCoproducer(coproducers)
  const active = coproducer?.status === 'ATIVO'
  const pending = coproducer?.status === 'PENDENTE'

  return (
    <div className={funnelCoproductionNotice}>
      <Handshake size={18} className={funnelCoproductionNoticeIcon} aria-hidden="true" />
      <div className={funnelCoproductionNoticeBody}>
        <div className={funnelCoproductionNoticeHeader}>
          <span className={funnelCoproductionNoticeTitle}>Coprodução deste funil</span>
          <StatusBadge
            size="sm"
            tone={active ? 'success' : pending ? 'awaiting' : 'neutral'}
            label={
              active ? 'Ativa em todo o funil' : pending ? 'Aguardando aceite' : 'Sem coprodutor'
            }
          />
        </div>
        {active ? (
          <span>
            <strong>{coproducer.email}</strong> recebe{' '}
            <strong>{percentageLabel(coproducer.percentage)}</strong> no produto principal, order
            bumps, upsells e downsells.
          </span>
        ) : pending ? (
          <span>
            O convite de <strong>{coproducer.email}</strong> para{' '}
            <strong>{percentageLabel(coproducer.percentage)}</strong> ainda precisa ser aceito. Até
            lá, nenhuma etapa deste funil terá coprodutor.
          </span>
        ) : (
          <span>
            O produto principal não possui coprodutor, então nenhuma etapa deste funil terá
            coprodução.
          </span>
        )}
        <span className={funnelCoproductionNoticeFootnote}>
          A coprodução própria de um produto adicionado só vale quando ele for o produto principal
          de outra venda.
        </span>
      </div>
    </div>
  )
}

export function FunnelTargetCoproductionNotice({
  funnelProduct,
  targetProduct,
}: {
  funnelProduct: Pick<Product, 'id' | 'coproducers'>
  targetProduct: Pick<Product, 'id' | 'name'>
}) {
  const isFunnelProduct = targetProduct.id === funnelProduct.id
  const targetCoproductionResult = useQuery({
    ...coproductionForProductQuery(targetProduct.id),
    enabled: !isFunnelProduct,
  })
  const targetCoproduction = targetCoproductionResult.data

  if (
    isFunnelProduct ||
    targetCoproduction === null ||
    targetCoproduction === undefined ||
    targetCoproduction.status === 'REMOVIDO'
  ) {
    return null
  }

  const targetCoproducer = inviteToCoproducer(targetCoproduction)
  const funnelCoproducer = currentCoproducer(funnelProduct.coproducers)
  const activeFunnelCoproducer = funnelCoproducer?.status === 'ATIVO' ? funnelCoproducer : undefined

  return (
    <div className={funnelStageNotice}>
      <Handshake size={18} className={funnelStageNoticeIcon} aria-hidden="true" />
      <span>
        <strong>{targetProduct.name}</strong>{' '}
        {targetCoproducer.status === 'ATIVO'
          ? 'tem coprodução própria ativa'
          : 'tem um convite de coprodução pendente'}
        , mas essa configuração não será usada nesta etapa.{' '}
        {activeFunnelCoproducer ? (
          <>
            Este funil segue <strong>{activeFunnelCoproducer.email}</strong> com{' '}
            <strong>{percentageLabel(activeFunnelCoproducer.percentage)}</strong>.
          </>
        ) : (
          <>
            Este funil não possui coprodutor porque o produto principal não possui coprodução ativa.
          </>
        )}
      </span>
    </div>
  )
}
